import React, { useMemo } from "react";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useFormikContext, FieldArray } from "formik";
import {
  MultipleExpenseFormData,
  OtherExpenseEntry,
  PaymentDetail,
  PAYMENT_MODES,
  PAYMENT_TYPES,
} from "../types";
import Paper from "@mui/material/Paper";
import { formatCurrency, formatDate } from "../../../utils/utils";
import IconButton from "@mui/material/IconButton";

interface PaymentDetailsFieldsProps {
  index: number;
}

const PaymentDetailsFields: React.FC<PaymentDetailsFieldsProps> = ({
  index,
}) => {
  const { values, errors, touched, setFieldValue } =
    useFormikContext<MultipleExpenseFormData>();
  const expense = values.expenses[index];
  const payments = expense.payments || [];
  const expenseErrors = errors.expenses?.[index] as any;
  const expenseTouched = touched.expenses?.[index] as any;
  const paymentErrors = expenseErrors?.payments as any;
  const paymentTouched = expenseTouched?.payments as any;

  // Calculate total of non-deleted payments using useMemo
  const totalPaidAmount = useMemo(
    () =>
      payments
        .filter((p) => !p.isDeleted)
        .reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
    [payments]
  );

  // Calculate remaining amount using useMemo
  const remainingAmount = useMemo(
    () => Number(expense.amount) - totalPaidAmount,
    [expense.amount, totalPaidAmount]
  );

  const handlePaymentChange = (
    paymentIndex: number,
    field: string,
    value: string | boolean
  ) => {
    setFieldValue(`expenses.${index}.payments.${paymentIndex}.isEdited`, true);
    setFieldValue(`expenses.${index}.payments.${paymentIndex}.${field}`, value);
  };

  // Format ISO date to YYYY-MM-DD
  const formatDateForInput = (isoDate: string) => {
    if (!isoDate) return "";
    return isoDate.split("T")[0];
  };

  const newPayment: PaymentDetail = {
    type: "ADVANCE",
    mode: "CASH",
    amount: 0,
    date: new Date().toISOString().split("T")[0],
    location: "",
    receivedFrom: "",
    remarks: "",
    status: "PENDING",
    isEdited: false,
    isDeleted: false,
    isNew: true,
    ...(expense?.id ? { otherExpenseId: expense.id } : {}),
  };

  return (
    <FieldArray name={`expenses.${index}.payments`}>
      {({ push, remove }) => (
        <>
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontSize: "0.75rem" }}>
                Payment Details
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography
                  variant="caption"
                  color={remainingAmount < 0 ? "error" : "textSecondary"}
                >
                  Balance: {formatCurrency(remainingAmount)}
                  {remainingAmount < 0 && " (Exceeds expense amount)"}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Total Paid: {formatCurrency(totalPaidAmount)}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {payments.length === 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "80px",
                  borderRadius: 1,
                  mt: 1,
                }}
              >
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => push(newPayment)}
                  variant="outlined"
                  size="small"
                  sx={{
                    textTransform: "none",
                    px: 2,
                    py: 0.5,
                    fontSize: "0.75rem",
                  }}
                >
                  Add Payment Details
                </Button>
              </Box>
            </Grid>
          )}

          {payments.map((payment, paymentIndex) => (
            <React.Fragment key={paymentIndex}>
              {!payment.isDeleted ? (
                <>
                  <Grid item xs={12}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", mb: 0.5 }}
                    >
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        Payment #{paymentIndex + 1}
                      </Typography>
                      <IconButton
                        color="error"
                        onClick={() => {
                          if (payment.id) {
                            handlePaymentChange(
                              paymentIndex,
                              "isDeleted",
                              true
                            );
                          } else {
                            remove(paymentIndex);
                          }
                        }}
                        size="small"
                        sx={{ p: 0.25, ml: "auto" }}
                      >
                        <DeleteIcon sx={{ fontSize: "0.875rem" }} />
                      </IconButton>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      fullWidth
                      select
                      size="small"
                      label="Type"
                      name={`expenses.${index}.payments.${paymentIndex}.type`}
                      value={payment.type || ""}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "type",
                          e.target.value
                        )
                      }
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.type &&
                          paymentErrors?.[paymentIndex]?.type
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.type &&
                        paymentErrors?.[paymentIndex]?.type
                      }
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                      }}
                    >
                      {PAYMENT_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                          {type}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      fullWidth
                      select
                      size="small"
                      label="Mode"
                      name={`expenses.${index}.payments.${paymentIndex}.mode`}
                      value={payment.mode || ""}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "mode",
                          e.target.value
                        )
                      }
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.mode &&
                          paymentErrors?.[paymentIndex]?.mode
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.mode &&
                        paymentErrors?.[paymentIndex]?.mode
                      }
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                      }}
                    >
                      {PAYMENT_MODES.map((mode) => (
                        <MenuItem key={mode} value={mode}>
                          {mode}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      type="text"
                      label="Amount"
                      name={`expenses.${index}.payments.${paymentIndex}.amount`}
                      value={
                        payment.amount
                          ? Number(payment.amount).toLocaleString("en-IN")
                          : ""
                      }
                      onChange={(e) => {
                        const value = e.target.value.replace(/,/g, "");
                        if (value === "" || /^\d*\.?\d*$/.test(value)) {
                          handlePaymentChange(paymentIndex, "amount", value);
                        }
                      }}
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.amount &&
                          paymentErrors?.[paymentIndex]?.amount
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.amount &&
                        paymentErrors?.[paymentIndex]?.amount
                      }
                      InputProps={{
                        startAdornment: (
                          <Typography
                            component="span"
                            sx={{ mr: 0.5, fontSize: "0.75rem" }}
                          >
                            ₹
                          </Typography>
                        ),
                        inputProps: {
                          inputMode: "decimal",
                        },
                      }}
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      required
                      fullWidth
                      size="small"
                      type="date"
                      label="Date"
                      name={`expenses.${index}.payments.${paymentIndex}.date`}
                      value={formatDateForInput(payment.date) || ""}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "date",
                          e.target.value
                        )
                      }
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.date &&
                          paymentErrors?.[paymentIndex]?.date
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.date &&
                        paymentErrors?.[paymentIndex]?.date
                      }
                      InputLabelProps={{ shrink: true }}
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                        "& .MuiFormHelperText-root": { fontSize: "0.65rem" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Location"
                      name={`expenses.${index}.payments.${paymentIndex}.location`}
                      value={payment.location}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "location",
                          e.target.value
                        )
                      }
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.location &&
                          paymentErrors?.[paymentIndex]?.location
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.location &&
                        paymentErrors?.[paymentIndex]?.location
                      }
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Paid To"
                      name={`expenses.${index}.payments.${paymentIndex}.receivedFrom`}
                      value={payment.receivedFrom}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "receivedFrom",
                          e.target.value
                        )
                      }
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.receivedFrom &&
                          paymentErrors?.[paymentIndex]?.receivedFrom
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.receivedFrom &&
                        paymentErrors?.[paymentIndex]?.receivedFrom
                      }
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                      }}
                    />
                  </Grid>

                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Remarks"
                      name={`expenses.${index}.payments.${paymentIndex}.remarks`}
                      value={payment.remarks}
                      onChange={(e) =>
                        handlePaymentChange(
                          paymentIndex,
                          "remarks",
                          e.target.value
                        )
                      }
                      multiline
                      error={Boolean(
                        paymentTouched?.[paymentIndex]?.remarks &&
                          paymentErrors?.[paymentIndex]?.remarks
                      )}
                      helperText={
                        paymentTouched?.[paymentIndex]?.remarks &&
                        paymentErrors?.[paymentIndex]?.remarks
                      }
                      sx={{
                        "& .MuiInputBase-root": { fontSize: "0.75rem" },
                        "& .MuiInputLabel-root": { fontSize: "0.75rem" },
                      }}
                    />
                  </Grid>
                </>
              ) : (
                <Grid item xs={12}>
                  <Paper
                    sx={{
                      p: 1,
                      backgroundColor: "#f5f5f5",
                      border: "1px solid #e0e0e0",
                      position: "relative",
                    }}
                  >
                    <Button
                      size="small"
                      sx={{
                        position: "absolute",
                        top: 4,
                        right: 4,
                        p: 0.5,
                        fontSize: "0.75rem",
                        fontStyle: "italic",
                      }}
                      onClick={() => {
                        handlePaymentChange(paymentIndex, "isDeleted", false);
                      }}
                    >
                      Undo Delete
                    </Button>

                    <Grid container spacing={1}>
                      <Grid item xs={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Type
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {payment.type}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Mode
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {payment.mode}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Amount
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          ₹{payment.amount}
                        </Typography>
                      </Grid>
                      <Grid item xs={1}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Date
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {formatDate(payment.date)}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Location
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {payment.location}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Paid To
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {payment.receivedFrom}
                        </Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                        >
                          Remark
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {payment.remarks}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </React.Fragment>
          ))}

          {payments.length > 0 && (
            <Grid item xs={12}>
              <Button
                startIcon={<AddIcon sx={{ fontSize: "0.875rem" }} />}
                onClick={() => push(newPayment)}
                sx={{
                  my: 0.5,
                  fontSize: "0.75rem",
                  textTransform: "none",
                  color: "primary.main",
                  minWidth: "auto",
                  "&:hover": {
                    backgroundColor: "transparent",
                    textDecoration: "underline",
                  },
                }}
              >
                Add another payment
              </Button>
            </Grid>
          )}
        </>
      )}
    </FieldArray>
  );
};

export default PaymentDetailsFields;
